import { saveOAuthReturn } from './oauthReturn';
import { logger } from '../utils/logger';
// import { toast } from 'react-toastify';
import platformService from '../services/platformService';
import { API_URL } from '../services/api';
import {
  ImportDateRange,
  sanitizeImportDateRange,
  getDefaultImportDateRange,
} from './importDateRange';

interface QuickBooksImportOptions {
  source: string;
  user: {
    id: number;
    email: string;
    company_id?: number | null;
    primary_company_id?: number | null;
  };
  setUploadError: (msg: string | null) => void;
  setImporting: (val: boolean) => void;
  navigate: (path: string, opts?: any) => void;
  companyId?: number;
  dateRange?: ImportDateRange;
}

interface QuickBooksOAuthResponse {
  auth_url?: string;
  detail?: string;
  [key: string]: any;
}

/**
 * Centralized QuickBooks import/connect handler for all flows
 * Ensures proper company context and error handling
 */
export const handleQuickBooksImport = async ({
  source,
  user,
  setUploadError,
  setImporting,
  navigate,
  companyId,
  dateRange
}: QuickBooksImportOptions): Promise<void> => {
  logger.log('[QBO Import] handler called with:', { source, user, companyId });
  setImporting(true);
  setUploadError(null);

  // Get OAuth URL from backend using authenticated API
  const fetchQuickBooksAuthUrl = async (): Promise<void> => {
    const token = localStorage.getItem('token');
    if (!token) {
      logger.error('[QBO Import] No authentication token found.');
      setUploadError('No authentication token found. Please log in again.');
      setImporting(false);
      return;
    }
    const targetCompanyId = companyId ?? user.primary_company_id ?? user.company_id;
    if (!targetCompanyId) {
      logger.error('[QBO Import] No company associated with this account.');
      setUploadError('No company associated with this account. Please set up your company first.');
      setImporting(false);
      return;
    }
    try {
      const apiUrl = `${API_URL}/api/v1/platforms/quickbooks/oauth/authorize`;
      const { startDate, endDate } = sanitizeImportDateRange(dateRange);
      const fetchPayload = {
        company_id: String(targetCompanyId),
        redirect_uri: `${window.location.origin}/quickbooks/callback`,
        source: source,
        start_date: startDate,
        end_date: endDate,
      };
      logger.log('[QBO Import] Fetching OAuth URL with:', { apiUrl, fetchPayload });
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
        },
        body: JSON.stringify(fetchPayload)
      });
      logger.log('[QBO Import] OAuth fetch response status:', response.status);
      const responseData: QuickBooksOAuthResponse = await response.json().catch(() => ({}));
      logger.log('[QBO Import] OAuth fetch response data:', responseData);
      if (!response.ok) {
        logger.error('[QBO Import] QuickBooks OAuth error:', {
          status: response.status,
          statusText: response.statusText,
          error: responseData
        });
        setUploadError(responseData.detail || 'Failed to get QuickBooks OAuth URL');
        setImporting(false);
        return;
      }
      if (!responseData.auth_url) {
        logger.error('[QBO Import] No auth URL returned from server');
        setUploadError('No auth URL returned from server');
        setImporting(false);
        return;
      }
      if (responseData.auth_url) {
        saveOAuthReturn('quickbooks', source, targetCompanyId);
        window.location.href = responseData.auth_url;
        return;
      } else {
        logger.error('[QBO Import] No auth URL provided in the response');
        setUploadError('No auth URL provided in the response');
        setImporting(false);
        return;
      }
    } catch (error: any) {
      logger.error('[QBO Import] QuickBooks OAuth fetch error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'QuickBooks OAuth failed';
      setUploadError(errorMessage);
      setImporting(false);
      return;
    }
  };
  await fetchQuickBooksAuthUrl();
  logger.log('[QBO Import] handler completed (should not reach here if redirecting)');
};

/**
 * Import QuickBooks sales transactions for a company after OAuth
 * Shows progress using setImporting and setUploadError
 */
export const importQuickBooksTransactions = async ({
  companyId,
  setImporting,
  setUploadError,
  onProgress,
  onComplete
}: {
  companyId: number,
  setImporting: (val: boolean) => void,
  setUploadError: (msg: string | null) => void,
  onProgress?: (msg: string) => void,
  onComplete?: (success: boolean, error?: string) => void
}) => {
  try {
    setImporting(true);
    setUploadError(null); // Changed from setImportError to setUploadError
    onProgress && onProgress('Starting QuickBooks import...');
    // Find the QuickBooks integration for this company
    const integrations = await platformService.listIntegrations(companyId);
    const qbIntegration = integrations.find(
      (i) => i.platform?.name?.toLowerCase() === 'quickbooks' && i.is_active && i.access_token
    );
    if (!qbIntegration) {
      setImporting(false);
      setUploadError && setUploadError('No active QuickBooks integration found for this company.'); // Changed from setImportError to setUploadError
      onComplete && onComplete(false, 'No active QuickBooks integration found.');
      return;
    }
    onProgress && onProgress('Importing sales transactions from QuickBooks...');
    // Start the import (sync transactions) - default last 90 days
    const { startDate: startISO, endDate: endISO } = getDefaultImportDateRange();
    try {
      // Keep TS happy with expected shape; service will normalize keys
      await platformService.syncTransactions(qbIntegration.id, {
        startDate: startISO as any,
        endDate: endISO as any,
        background: true
      } as any);
    } catch (err: any) {
      setImporting(false);
      setUploadError && setUploadError(err?.message || 'QuickBooks import failed.');
      onComplete && onComplete(false, err?.message || 'QuickBooks import failed.');
      return;
    }
    onProgress && onProgress('QuickBooks import complete.');
    setImporting(false);
    onComplete && onComplete(true);
  } catch (err: any) {
    setImporting(false);
    setUploadError && setUploadError(err?.message || 'QuickBooks import failed.'); // Changed from setImportError to setUploadError
    onComplete && onComplete(false, err?.message || 'QuickBooks import failed.');
  }
};
